import React, { useEffect, useState, useRef } from "react"
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material"
import { UText } from "@/ui-component"
import debounce from "lodash/debounce"
import { useAppDispatch, useAppSelector } from "@/core/app/hooks"
import { fetchWorkflowSettings, updateWorkflowSettings } from "@/core/app/slices/user/userApis"
import { isEqual } from "lodash"
import { setWorkflow } from "@/core/app/slices/user/userSlice"
import { IUserServiceState } from "@/core/app/slices/user/user.type"

function OfficeWorkflow() {
  const dispatch = useAppDispatch()
  
  const { workflow } = useAppSelector(state => state.userService)

  const [data, setData] = useState<IUserServiceState["workflow"]>({
    showWorkflow: workflow.showWorkflow,
    phase: workflow.phase
  })

  const debouncedUpdate = useRef(
    debounce((currentData: IUserServiceState["workflow"]) => {
      if (isEqual(workflow, currentData)) return
      
      dispatch(updateWorkflowSettings({
        ShowWorkflow: Number(currentData.showWorkflow),
        phase: currentData.phase.map((item) => ({
          id: item.id,
          value: item.value
        }))
      })).then(() => {
        dispatch(setWorkflow(currentData))
      })
    }, 1000)
  ).current

  useEffect(()=>{
    dispatch(fetchWorkflowSettings())
  },[])

  useEffect(()=>{
    if( isEqual(workflow, data) ) return
    setData(workflow)
  },[workflow])

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    label?: { id: number; value: string; color: string },
  ) => {
    const { name, value } = event.target
    switch (name) {
      case "phase":
        if (!label) return
        const updatedPhase = data.phase.map((flagLabel) =>
          flagLabel.id === label.id ? { ...flagLabel, value } : flagLabel,
        );
        const phaseData = {
          ...data,
          phase: updatedPhase,
        }
        setData(phaseData)
        debouncedUpdate(phaseData)
        break
      case "showWorkflow":
        const workflowData = {
          ...data,
          showWorkflow: value === "Yes",
        }
        setData(workflowData)
        debouncedUpdate(workflowData)
        break
    }
  }

  return (
    <Grid sx={{ width: "600px", p: 4 }}>
      <UText
        variant={"h4"}
        component={"div"}
        sxProp={{ textAlign: "center", pb: 2 }}
      >
        {"Office Workflow"}
      </UText>
      <Grid
        item
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "start",
          mb: 2,
        }}
      >
        <UText variant={"h6"} sxProp={{ color: "#000000" }}>
          {"Turn on Internal Workflow"}
        </UText>
        <FormControl>
          <RadioGroup
            aria-labelledby="showWorkflow"
            name="showWorkflow"
            sx={{ py: 1, px: 3, flexDirection: "row" }}
            value={data.showWorkflow ? "Yes" : "No"}
            onChange={(event, value) => handleChange(event as React.ChangeEvent<HTMLInputElement>)}
          >
            {["Yes", "No"].map((option) => (
              <FormControlLabel
                key={option}
                sx={{
                  color: (theme) => theme.palette.text.primary,
                  width: "fit-content",
                }}
                value={option}
                control={<Radio />}
                label={option}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Grid>
      { !!data.showWorkflow && (<Grid
        item
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "start",
          mb: 3,
        }}
      >
        <UText variant={"h6"} sxProp={{ color: "#000000" }}>
          {"Flag Labels"}
        </UText>
        <Grid container spacing={3} direction={"column"} padding={3}>
          {data.phase.map((label, index) => (
            <Grid item key={index}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: label.color,
                  }}
                />
                <TextField
                  name={"phase"}
                  defaultValue={label.value}
                  variant="outlined"
                  onChange={(event) => {
                    handleChange(event, label)
                  }}
                  sx={{
                    // py: "0px",
                    flexGrow: 1,
                    "& .MuiInputBase-input": {
                      py: "8px",
                    },
                  }}
                  inputProps={{ maxLength: 12 }}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Grid>)}
    </Grid>
  )
}

export default OfficeWorkflow
